import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2, AlertCircle, Copy, PanelLeftClose, PanelLeft, PanelRightClose, PanelRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/Badges';
import { GroupTree } from '../components/editor/GroupTree';
import { ConceptsTable } from '../components/editor/ConceptsTable';
import { SummaryPanel } from '../components/editor/SummaryPanel';
import { ApuDialog } from '../components/editor/ApuDialog';
import { ActionsMenu } from '../components/editor/ActionsMenu';
import { useQuotationEditor } from '../hooks/useQuotationEditor';
import api from '../lib/api';
import { fmtMoney } from '../lib/format';

const AutosaveIndicator = ({ state }) => (
  <div className="flex items-center gap-1.5 text-[12px]" data-testid="autosave-status">
    {state === 'saving' && (<><Loader2 size={13} className="animate-spin text-muted-foreground" /><span className="text-muted-foreground">Guardando…</span></>)}
    {state === 'saved' && (<><CheckCircle2 size={13} className="text-primary" /><span className="text-primary">Guardado</span></>)}
    {state === 'error' && (<><AlertCircle size={13} className="text-destructive" /><span className="text-destructive">Error al guardar</span></>)}
    {state === 'idle' && (<span className="text-muted-foreground/60">Autoguardado activo</span>)}
  </div>
);

export default function EditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editor = useQuotationEditor(id);
  const { quotation, isLoading, saveState, frozen } = editor;

  const [selectedConceptId, setSelectedConceptId] = useState(null);
  const [apuConcept, setApuConcept] = useState(null);
  const [showTree, setShowTree] = useState(true);
  const [showSummary, setShowSummary] = useState(true);

  // Diálogo nuevo concepto
  const [conceptDialogOpen, setConceptDialogOpen] = useState(false);
  const [conceptGroupId, setConceptGroupId] = useState(null);
  const [conceptForm, setConceptForm] = useState({ descripcion: '', unidad: 'pza', cantidad: 1, indirect_percentage: 0, financing_percentage: 0, utility_percentage: 0 });

  // Diálogo concepto desde catálogo
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogConceptId, setCatalogConceptId] = useState('');
  const [catalogCantidad, setCatalogCantidad] = useState(1);

  const scope = quotation?.quotation_type === 'pv' ? 'pv' : 'prefab';
  const { data: catalogConcepts } = useQuery({
    queryKey: ['catalog', 'concepts', scope, ''],
    queryFn: async () => (await api.get('/catalog/concepts', { params: { scope, limit: 300 } })).data,
    enabled: catalogDialogOpen,
  });

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => (await api.get('/settings')).data,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12" />
        <div className="flex gap-4"><Skeleton className="h-[60vh] w-72" /><Skeleton className="h-[60vh] flex-1" /><Skeleton className="h-[60vh] w-80" /></div>
      </div>
    );
  }

  if (!quotation) return <div className="text-muted-foreground">Cotización no encontrada</div>;

  const openAddConcept = (groupId) => {
    setConceptGroupId(groupId);
    // Prellenar porcentajes desde Configuración (defaults por módulo)
    const d = (appSettings?.apply_defaults_to_new_concepts !== false && appSettings?.concept_defaults?.[scope]) || {};
    setConceptForm({
      descripcion: '', unidad: 'pza', cantidad: 1,
      indirect_percentage: d.indirect_pct ?? 0,
      financing_percentage: d.financing_pct ?? 0,
      utility_percentage: d.utility_pct ?? 0,
    });
    setConceptDialogOpen(true);
  };

  const openAddFromCatalog = (groupId) => {
    setConceptGroupId(groupId);
    setCatalogConceptId('');
    setCatalogCantidad(1);
    setCatalogDialogOpen(true);
  };

  const submitConcept = () => {
    editor.addConcept.mutate({ group_id: conceptGroupId, ...conceptForm, cantidad: Number(conceptForm.cantidad) });
    setConceptDialogOpen(false);
  };

  const submitCatalogConcept = () => {
    editor.addConceptFromCatalog.mutate({ group_id: conceptGroupId, catalog_id: catalogConceptId, cantidad: Number(catalogCantidad) });
    setCatalogDialogOpen(false);
  };

  const backTo = quotation.quotation_type === 'pv' ? '/cotizaciones-pv' : '/cotizaciones';

  return (
    <div className="-m-5 md:-m-6 flex flex-col" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Header del editor */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-card/40">
        <div className="flex items-center gap-2.5 min-w-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate(backTo)} data-testid="back-to-list-button">
            <ArrowLeft size={17} />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[12px] text-muted-foreground">{quotation.quotation_number}</span>
              <StatusBadge status={quotation.status} />
            </div>
            <h2 className="text-[15px] font-semibold truncate max-w-[420px]" title={quotation.nombre} data-testid="editor-quotation-name">{quotation.nombre}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <AutosaveIndicator state={saveState} />
          <div className="h-5 w-px bg-border" />
          <span className="money text-[14px] font-semibold text-primary" data-testid="editor-header-total">{fmtMoney(quotation.totals?.total, quotation.moneda)}</span>
          <div className="h-5 w-px bg-border" />
          <ActionsMenu
            quotation={quotation}
            frozen={frozen}
            onImported={() => {
              queryClient.invalidateQueries({ queryKey: ['quotation-full', id] });
              queryClient.invalidateQueries({ queryKey: ['quotations'] });
            }}
          />
          <div className="h-5 w-px bg-border" />
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowTree((v) => !v)} data-testid="toggle-tree-button" title="Mostrar/ocultar árbol">
            {showTree ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowSummary((v) => !v)} data-testid="toggle-summary-button" title="Mostrar/ocultar resumen">
            {showSummary ? <PanelRightClose size={16} /> : <PanelRight size={16} />}
          </Button>
        </div>
      </div>

      {/* 3 zonas */}
      <div className="flex flex-1 min-h-0">
        {showTree && (
          <div className="w-[300px] min-w-[260px] border-r border-border bg-card/30 hidden md:block">
            <GroupTree
              quotation={quotation}
              frozen={frozen}
              selectedConceptId={selectedConceptId}
              onSelectConcept={setSelectedConceptId}
              editor={editor}
              onAddConcept={openAddConcept}
              onAddFromCatalog={openAddFromCatalog}
            />
          </div>
        )}
        <div className="flex-1 min-w-0 bg-card/10">
          <ConceptsTable
            quotation={quotation}
            frozen={frozen}
            selectedConceptId={selectedConceptId}
            onOpenApu={(c) => setApuConcept(c)}
            editor={editor}
          />
        </div>
        {showSummary && (
          <div className="w-[340px] min-w-[300px] border-l border-border bg-card/30 hidden lg:block">
            <SummaryPanel quotation={quotation} frozen={frozen} editor={editor} />
          </div>
        )}
      </div>

      {/* Diálogo APU */}
      <ApuDialog
        open={!!apuConcept}
        onOpenChange={(v) => { if (!v) setApuConcept(null); }}
        concept={apuConcept}
        quotation={quotation}
        frozen={frozen}
        editor={editor}
      />

      {/* Diálogo nuevo concepto */}
      <Dialog open={conceptDialogOpen} onOpenChange={setConceptDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-concept-dialog">
          <DialogHeader><DialogTitle>Nuevo concepto</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Descripción *</Label>
              <Input value={conceptForm.descripcion} onChange={(e) => setConceptForm((f) => ({ ...f, descripcion: e.target.value }))} data-testid="concept-descripcion-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Unidad</Label><Input value={conceptForm.unidad} onChange={(e) => setConceptForm((f) => ({ ...f, unidad: e.target.value }))} data-testid="concept-unidad-input" /></div>
              <div className="space-y-1.5"><Label>Cantidad</Label><Input type="number" step="0.0001" value={conceptForm.cantidad} onChange={(e) => setConceptForm((f) => ({ ...f, cantidad: e.target.value }))} data-testid="concept-cantidad-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-[12px]">% Indirectos</Label><Input type="number" step="0.01" value={conceptForm.indirect_percentage} onChange={(e) => setConceptForm((f) => ({ ...f, indirect_percentage: Number(e.target.value) }))} data-testid="concept-ind-input" /></div>
              <div className="space-y-1.5"><Label className="text-[12px]">% Financ.</Label><Input type="number" step="0.01" value={conceptForm.financing_percentage} onChange={(e) => setConceptForm((f) => ({ ...f, financing_percentage: Number(e.target.value) }))} data-testid="concept-fin-input" /></div>
              <div className="space-y-1.5"><Label className="text-[12px]">% Utilidad</Label><Input type="number" step="0.01" value={conceptForm.utility_percentage} onChange={(e) => setConceptForm((f) => ({ ...f, utility_percentage: Number(e.target.value) }))} data-testid="concept-uti-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConceptDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submitConcept} disabled={!conceptForm.descripcion} data-testid="create-concept-button">Crear concepto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo concepto desde catálogo */}
      <Dialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen}>
        <DialogContent className="max-w-md" data-testid="catalog-concept-dialog">
          <DialogHeader><DialogTitle>Concepto desde catálogo ({scope})</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Concepto</Label>
              <Select value={catalogConceptId} onValueChange={setCatalogConceptId}>
                <SelectTrigger data-testid="catalog-concept-select"><SelectValue placeholder="Selecciona un concepto" /></SelectTrigger>
                <SelectContent>
                  {(catalogConcepts?.items || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.numero} · {c.descripcion} ({c.unidad})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(catalogConcepts?.items || []).length === 0 && (
                <p className="text-[12px] text-muted-foreground">No hay conceptos en el catálogo {scope}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Cantidad</Label>
              <Input type="number" step="0.0001" value={catalogCantidad} onChange={(e) => setCatalogCantidad(e.target.value)} data-testid="catalog-concept-cantidad-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submitCatalogConcept} disabled={!catalogConceptId} data-testid="add-catalog-concept-button">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
